import { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { useCart } from "~/hooks/cart";
import { useLocationDeals } from "~/hooks/deals";
import { useCartMenu } from "~/hooks/menu-v2";
import { useOrderingActive } from "~/hooks/ordering";
import { CartIcon } from "~/icons";
import { mapCartDetails } from "~/utils/cart";
import { Box } from "../Box/Box";
import { Currency } from "../Currency/Currency";
import { Hidden } from "../Hidden/Hidden";
import { Inline } from "../Inline/Inline";
import { OnlineStatusIndicator } from "../OnlineStatusIndicator/OnlineStatusIndicator";
import { SafeAreaBottom } from "../SafeArea/SafeArea";
import { Text } from "../Text/Text";

export interface MobileCheckoutButtonProps {
  locationRef: string;
  onPress: () => void;
}

export function MobileCheckoutButton({
  locationRef,
  onPress,
}: MobileCheckoutButtonProps): React.ReactNode | null {
  const { cart, dispatchOption, timeSlot } = useCart(locationRef);

  const { loadingOrdering, deliveryActive, pickupActive, serviceActive } =
    useOrderingActive(locationRef, timeSlot);

  const { menu, isLoading: loadingMenu } = useCartMenu(
    dispatchOption,
    timeSlot,
    cart.items.map((i) => i.productId),
  );

  const { deals, loading: loadingDeals } = useLocationDeals(
    locationRef,
    dispatchOption,
  );

  const { cartTotalPrice, itemCount } = useMemo(
    () => mapCartDetails(cart.items, menu, deals),
    [cart.items, menu, deals],
  );

  const loading =
    loadingOrdering || loadingMenu || loadingDeals || cart.checkingCart;

  return (
    <Box
      position="sticky"
      style={{ bottom: 0, width: "100vw" }}
      background="info"
    >
      {!loading && (
        <OnlineStatusIndicator
          dispatchOption={dispatchOption}
          deliveryActive={deliveryActive || false}
          pickupActive={pickupActive || false}
          serviceActive={serviceActive || false}
          safeBottom={true}
        />
      )}
      {itemCount > 0 ? (
        <Hidden above="tablet">
          <Box background="primary">
            <SafeAreaBottom>
              <Box
                background="primary"
                display="flex"
                paddingX="5"
                // same height as jets header
                style={{ height: 75 }}
                alignItems="center"
                onClick={onPress}
              >
                <Inline space="2" alignY="center">
                  {/* Wrap Icon in Text component to add color to icon through inheritance from parent */}
                  <Text color="overPrimary">
                    <CartIcon />
                  </Text>
                  <Text weight="semi-strong" size="xxlarge" color="overPrimary">
                    {itemCount}
                  </Text>
                </Inline>

                <Box style={{ marginLeft: "auto" }}>
                  <Text weight="semi-strong" size="xxlarge" color="overPrimary">
                    {loading ? (
                      <Skeleton style={{ opacity: 0.2 }} width={80} />
                    ) : (
                      <Currency price={cartTotalPrice} />
                    )}
                  </Text>
                </Box>
              </Box>
            </SafeAreaBottom>
          </Box>
        </Hidden>
      ) : null}
    </Box>
  );
}
